import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Tooltip,
  CircularProgress
} from "@mui/material";
import { Download } from "lucide-react";
import { getFilePresignedViewUrl } from "../../../../services/eventUploadsService";
import { downloadFileFromUrl } from "../../../../utils/fileDownloader";

/**
 * InstituteLogoCell
 * Provides on-demand 1-click download of the institute logo without upfront image fetching.
 * S3 presigned download URL is ONLY requested when the designer clicks Download, saving S3 request costs.
 *
 * @param {string} logoKey - S3 key or URL of the institute logo
 * @param {string} instituteName - Name of the institute (used for file naming)
 * @param {boolean} iconOnly - When true, renders an IconButton with Tooltip
 */
const InstituteLogoCell = ({ logoKey, instituteName, iconOnly = false }) => {
  const [isDownloading, setIsDownloading] = useState(false);

  if (iconOnly && !logoKey) {
    return (
      <Tooltip title="No Institute Logo Available" arrow placement="top">
        <span>
          <IconButton
            size="small"
            disabled
            sx={{
              p: 0.65,
              borderRadius: "6px",
              border: "1px solid #e2e8f0",
              backgroundColor: "#f8fafc",
              color: "#cbd5e1"
            }}
          >
            <Download size={14} />
          </IconButton>
        </span>
      </Tooltip>
    );
  }

  if (!logoKey) {
    return (
      <Typography sx={{ color: "#94a3b8", fontSize: "0.75rem", fontStyle: "italic", textAlign: "center" }}>
        —
      </Typography>
    );
  }

  const handleDownload = async (e) => {
    e.stopPropagation();
    if (isDownloading) return;

    setIsDownloading(true);
    try {
      const cleanInstName = (instituteName || "Institute").replace(/[^a-zA-Z0-9_-]/g, "_");
      const extMatch = logoKey.match(/\.(png|jpe?g|svg|webp|gif)$/i);
      const ext = extMatch ? extMatch[0].toLowerCase() : ".png";
      const fileName = `${cleanInstName}_logo${ext}`;

      let downloadUrl = null;
      if (logoKey.startsWith("http://") || logoKey.startsWith("https://")) {
        downloadUrl = logoKey;
      } else {
        // Fetch presigned URL strictly on-demand with attachment content-disposition
        downloadUrl = await getFilePresignedViewUrl(logoKey, true, fileName);
      }

      if (downloadUrl) {
        await downloadFileFromUrl(downloadUrl, fileName);
      }
    } catch (err) {
      console.error("Failed to download institute logo:", err);
    } finally {
      setIsDownloading(false);
    }
  };

  if (iconOnly) {
    return (
      <Tooltip title={`Download ${instituteName || "Institute"} Logo`} arrow placement="top">
        <span>
          <IconButton
            size="small"
            disabled={isDownloading}
            onClick={handleDownload}
            sx={{
              p: 0.5,
              borderRadius: "6px",
              border: "1px solid #bfdbfe",
              backgroundColor: "#eff6ff",
              color: "#1877f2",
              transition: "all 0.15s ease",
              "&:hover": {
                backgroundColor: "#dbeafe",
                borderColor: "#1877f2"
              },
              "&.Mui-disabled": {
                backgroundColor: "#f1f5f9",
                borderColor: "#e2e8f0",
                color: "#94a3b8"
              }
            }}
          >
            {isDownloading ? (
              <CircularProgress size={14} color="inherit" />
            ) : (
              <Download size={14} />
            )}
          </IconButton>
        </span>
      </Tooltip>
    );
  }

  return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
      <Tooltip title={`Download ${instituteName || "Institute"} Logo`} arrow placement="top">
        <span>
          <Button
            size="small"
            variant="outlined"
            disabled={isDownloading}
            onClick={handleDownload}
            startIcon={
              isDownloading ? (
                <CircularProgress size={12} color="inherit" />
              ) : (
                <Download size={13} />
              )
            }
            sx={{
              fontSize: "0.74rem",
              fontWeight: 600,
              textTransform: "none",
              borderRadius: "6px",
              py: 0.35,
              px: 1.2,
              color: "#1877f2",
              borderColor: "#bfdbfe",
              backgroundColor: "#eff6ff",
              "&:hover": {
                backgroundColor: "#dbeafe",
                borderColor: "#1877f2"
              },
              "&.Mui-disabled": {
                backgroundColor: "#f1f5f9",
                borderColor: "#e2e8f0",
                color: "#94a3b8"
              }
            }}
          >
            {isDownloading ? "Downloading..." : "Download"}
          </Button>
        </span>
      </Tooltip>
    </Box>
  );
};

export default InstituteLogoCell;
